import React, { useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaFileAlt, FaFileExcel, FaFileImage, FaFilePdf, FaFileWord, FaPaperclip, FaTimes } from 'react-icons/fa';
import VisualizarArquivoModal from './VisualizarArquivoModal';
import '../css/financeiro-parcela-pagar.css';

/**
 * Campo de observação que aceita anexos inline, como o corpo de um e-mail: o print colado entra no ponto do cursor,
 * dentro do texto, e se arrasta dentro dele para mudar de lugar; Delete apaga. Os outros arquivos ficam no rodapé.
 * Anexa colando (Ctrl+V de um print), arrastando sobre o campo ou pelo botão Anexar. O Enter quebra a linha sempre,
 * mesmo dentro de um form que pula de campo com ele. A imagem abre na prévia de arquivos (VisualizarArquivoModal):
 * com um clique na leitura, com dois editando.
 *
 * O campo é um `contenteditable`, o editável nativo do HTML; o que sai dele não é HTML. Controlado: `value` =
 * { texto, anexos } e `onChange` devolve o value inteiro, lido do campo a cada mudança.
 * - `texto`: a observação, só texto; as imagens não entram nele.
 * - `anexos`: a lista, na ordem em que aparecem. Cada anexo é uma entidade com o arquivo e a linha:
 *     novo:  { id, arquivo, linha } — `arquivo` é o File do navegador, sem leitura nem conversão, e o `id` é provisório
 *            (1, 2, 3...) até o back gravar; quem salva manda por multipart (appendToFormData), nunca o binário no JSON
 *     salvo: { id, nome, link, linha } — o id real, com o link do arquivo gravado
 *   `linha`: o anexo aparece depois de tantas linhas do texto (0 = antes da primeira); `null` = sem posição, depois do
 *   texto. Cada anexo ocupa uma linha inteira: solto no meio de uma linha, ele fica depois dela. Anexos da mesma
 *   linha seguem a ordem da lista, que vai para o back como `ordem`.
 *
 * `anexosNoCorpo`: quais anexos ficam no texto; os outros, no rodapé.
 *   'imagens' (padrão) — as imagens, como num e-mail; os demais arquivos no rodapé
 *   'todos'            — imagens e arquivos, estes como etiqueta
 *   'nenhum'           — tudo no rodapé, como anexo de e-mail
 * `maxAnexos`: limite da lista; cheio, o anexo novo não entra e um aviso explica.
 * `readOnly`: a leitura, com a aparência normal do campo; `disabled`: a leitura com a aparência de desabilitado.
 * `semCaixa`: sem borda, fundo nem altura mínima, como o corpo de um e-mail; vale para editar e para a leitura.
 *
 *   <FinField label="Observação" full>
 *     <ObservacaoAnexos value={obs} onChange={setObs} />
 *   </FinField>
 */

const EXTENSOES_IMAGEM = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

const arquivoDe = (anexo) => (anexo?.arquivo instanceof File ? anexo.arquivo : null);

export const nomeDoAnexo = (anexo) => arquivoDe(anexo)?.name || anexo?.nome || 'arquivo';

export const ehImagem = (anexo) => {
    const arquivo = arquivoDe(anexo);
    return arquivo ? (arquivo.type || '').startsWith('image/') : EXTENSOES_IMAGEM.test(nomeDoAnexo(anexo));
};

// O print colado chega do navegador como "image.png": vira "print-<data>-<hora>" para os nomes não se repetirem.
// Arquivo copiado do Explorer chega com o nome dele e fica como está.
export const nomearColado = (arquivo, agora = new Date()) => {
    if (!/^image\.(png|jpe?g|gif|webp)$/i.test(arquivo.name)) return arquivo;
    const dois = (n) => String(n).padStart(2, '0');
    const data = `${agora.getFullYear()}${dois(agora.getMonth() + 1)}${dois(agora.getDate())}`;
    const hora = `${dois(agora.getHours())}${dois(agora.getMinutes())}${dois(agora.getSeconds())}`;
    const extensao = arquivo.name.split('.').pop();
    return new File([arquivo], `print-${data}-${hora}.${extensao}`, { type: arquivo.type, lastModified: arquivo.lastModified });
};

// O mesmo filtro do `accept` do input de arquivo, para o arrastar e o colar, que passam por fora dele.
export const aceita = (arquivo, accept) => {
    if (!accept) return true;
    const nome = (arquivo.name || '').toLowerCase();
    const tipo = (arquivo.type || '').toLowerCase();
    return accept.split(',').map((regra) => regra.trim().toLowerCase()).filter(Boolean).some((regra) => {
        if (regra.startsWith('.')) return nome.endsWith(regra);
        if (regra.endsWith('/*')) return tipo.startsWith(regra.slice(0, -1));
        return tipo === regra;
    });
};

const vaiNoCorpo = (anexo, anexosNoCorpo) => anexosNoCorpo === 'todos' || (anexosNoCorpo === 'imagens' && ehImagem(anexo));

// Lê o campo editável: o texto, linha a linha, e a linha de cada anexo, na ordem em que aparecem. Cada navegador
// monta as linhas do seu jeito (div por linha, <br>, texto solto na raiz), e a leitura aceita todos. Anexo no meio de
// uma linha fica depois dela, para o texto não ser cortado. `chavePorSrc` reconhece a imagem que o navegador recriou
// ao arrastar, se ela perder o data-anexo.
export const lerEditor = (raiz, chavePorSrc = new Map()) => {
    const linhas = [];
    const posicoes = [];
    let atual = null;
    const fechar = () => {
        if (atual !== null) linhas.push(atual);
        atual = null;
    };
    const visitar = (no) => {
        if (no.nodeType === 3) {
            atual = (atual ?? '') + no.nodeValue;
            return;
        }
        if (no.nodeType !== 1) return;
        const chave = no.dataset?.anexo || (no.tagName === 'IMG' ? chavePorSrc.get(no.getAttribute('src')) : null);
        if (chave) {
            posicoes.push({ chave, linha: atual ? linhas.length + 1 : linhas.length });
            return;
        }
        if (no.tagName === 'BR') {
            linhas.push(atual ?? '');
            atual = null;
            return;
        }
        const bloco = /^(DIV|P)$/.test(no.tagName);
        if (bloco) fechar();
        no.childNodes.forEach(visitar);
        if (bloco) fechar();
    };
    raiz.childNodes.forEach(visitar);
    fechar();
    return { texto: linhas.join('\n'), posicoes };
};

const tamanhoLegivel = (bytes) => {
    if (typeof bytes !== 'number') return null;
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

const iconeDoAnexo = (anexo) => {
    if (ehImagem(anexo)) return FaFileImage;
    const nome = nomeDoAnexo(anexo).toLowerCase();
    if (nome.endsWith('.pdf')) return FaFilePdf;
    if (/\.(xlsx?|xlsb|csv)$/.test(nome)) return FaFileExcel;
    if (/\.docx?$/.test(nome)) return FaFileWord;
    return FaFileAlt;
};

export default function ObservacaoAnexos({
    value = null,
    onChange = null,
    anexosNoCorpo = 'imagens',
    placeholder = 'Escreva a observação',
    accept = null,
    maxAnexos = null,
    disabled = false,
    readOnly = false,
    semCaixa = false,
}) {
    const editorRef = useRef(null);
    const inputRef = useRef(null);
    const urlsRef = useRef(new Map());
    const emitidoRef = useRef(null);
    const blocoGuardadoRef = useRef(null);
    const arrastoInternoRef = useRef(false);
    const [arrastando, setArrastando] = useState(false);
    const [previa, setPrevia] = useState(null);
    const texto = value?.texto ?? '';
    const anexos = value?.anexos || [];
    const editavel = !disabled && !readOnly;

    // Prévia dos File novos por URL temporária do navegador, sem ler o arquivo. O cache acompanha a lista: cria a URL
    // do File que entrou e libera a do que saiu. Fica no render, e não num efeito com estado, porque a lista pode
    // chegar nova a cada render (value={{ ... }}) e o efeito entraria em laço.
    const arquivosNovos = anexos.map(arquivoDe).filter(Boolean);
    urlsRef.current.forEach((url, arquivo) => {
        if (!arquivosNovos.includes(arquivo)) {
            URL.revokeObjectURL(url);
            urlsRef.current.delete(arquivo);
        }
    });
    const urlDe = (arquivo) => {
        if (!urlsRef.current.has(arquivo)) urlsRef.current.set(arquivo, URL.createObjectURL(arquivo));
        return urlsRef.current.get(arquivo);
    };
    arquivosNovos.forEach(urlDe);

    const linkDe = (anexo) => (arquivoDe(anexo) ? urlDe(arquivoDe(anexo)) : (anexo?.link || anexo?.url));
    const chaveDe = (anexo) => `${arquivoDe(anexo) ? 'novo' : 'salvo'}-${anexo.id}`;
    const corpo = anexos.filter((anexo) => vaiNoCorpo(anexo, anexosNoCorpo));
    const rodape = anexos.filter((anexo) => !vaiNoCorpo(anexo, anexosNoCorpo));
    const assinatura = (textoDe, corpoDe) => `${textoDe}\n--\n${corpoDe.map((anexo) => `${chaveDe(anexo)}:${anexo.linha ?? ''}`).join(',')}`;

    // O anexo dentro do campo: a imagem, ou a etiqueta do arquivo (no modo 'todos'), numa linha só dela.
    const blocoDoAnexo = (anexo) => {
        const linha = document.createElement('div');
        let elemento;
        if (ehImagem(anexo)) {
            elemento = document.createElement('img');
            elemento.src = linkDe(anexo) || '';
            elemento.alt = nomeDoAnexo(anexo);
            elemento.title = editavel
                ? `${nomeDoAnexo(anexo)}: arraste para mudar de lugar; dois cliques para ver; selecione e aperte Delete para tirar`
                : `${nomeDoAnexo(anexo)}: clique para ver`;
        } else {
            elemento = document.createElement('span');
            elemento.contentEditable = 'false';
            elemento.className = 'fin-pp__obs-anexo';
            elemento.textContent = nomeDoAnexo(anexo);
        }
        elemento.dataset.anexo = chaveDe(anexo);
        linha.appendChild(elemento);
        return linha;
    };

    // Monta o campo a partir do value: as linhas do texto e, entre elas, os anexos nas linhas deles.
    const montarEditor = (textoDe, corpoDe) => {
        const editor = editorRef.current;
        const linhasDoTexto = textoDe === '' ? [] : textoDe.split('\n');
        editor.innerHTML = '';
        for (let n = 0; n <= linhasDoTexto.length; n += 1) {
            const fim = n === linhasDoTexto.length;
            corpoDe
                .filter((anexo) => {
                    const linha = anexo.linha ?? linhasDoTexto.length;
                    return fim ? linha >= n : linha === n;
                })
                .forEach((anexo) => editor.appendChild(blocoDoAnexo(anexo)));
            if (!fim) {
                const linha = document.createElement('div');
                if (linhasDoTexto[n]) linha.textContent = linhasDoTexto[n];
                else linha.appendChild(document.createElement('br'));
                editor.appendChild(linha);
            }
        }
    };

    // O campo só é remontado quando o value chega de fora (abrir, trocar de registro); o que veio do próprio campo já
    // está nele, e remontar tiraria o cursor do lugar.
    useLayoutEffect(() => {
        if (!editorRef.current) return;
        const chegou = `${assinatura(texto, corpo)}\n${editavel}`;
        if (chegou === emitidoRef.current) return;
        montarEditor(texto, corpo);
        emitidoRef.current = chegou;
    });

    // Lê o campo e devolve o value: os anexos do texto na ordem em que aparecem, e os do rodapé.
    const emitir = (novos = []) => {
        const porChave = new Map([...anexos, ...novos].map((anexo) => [chaveDe(anexo), anexo]));
        const chavePorSrc = new Map([...anexos, ...novos].filter((anexo) => ehImagem(anexo)).map((anexo) => [linkDe(anexo), chaveDe(anexo)]));
        const lido = lerEditor(editorRef.current, chavePorSrc);
        const noTexto = lido.posicoes
            .filter(({ chave }, i, todos) => porChave.has(chave) && todos.findIndex((outro) => outro.chave === chave) === i)
            .map(({ chave, linha }) => ({ ...porChave.get(chave), linha }));
        const foraDoTexto = [...rodape, ...novos.filter((anexo) => !vaiNoCorpo(anexo, anexosNoCorpo))];
        emitidoRef.current = `${assinatura(lido.texto, noTexto)}\n${editavel}`;
        if (onChange) onChange({ texto: lido.texto, anexos: [...noTexto, ...foraDoTexto] });
    };

    // A linha de topo do campo em que está o cursor (ou a guardada quando o campo perdeu o foco); null = o fim.
    const blocoDe = (no, deslocamento) => {
        const editor = editorRef.current;
        if (!no || !editor.contains(no)) return null;
        if (no === editor) return editor.childNodes[deslocamento - 1] || null;
        let atual = no;
        while (atual.parentNode !== editor) atual = atual.parentNode;
        return atual;
    };
    const blocoDoCursor = () => {
        const selecao = window.getSelection();
        if (selecao && selecao.rangeCount) {
            const range = selecao.getRangeAt(0);
            const bloco = blocoDe(range.endContainer, range.endOffset);
            if (bloco || range.endContainer === editorRef.current) return bloco;
        }
        const guardado = blocoGuardadoRef.current;
        return guardado && editorRef.current.contains(guardado) ? guardado : null;
    };
    const guardarCursor = () => {
        const selecao = window.getSelection();
        if (selecao && selecao.rangeCount) {
            const range = selecao.getRangeAt(0);
            if (editorRef.current.contains(range.endContainer)) blocoGuardadoRef.current = blocoDe(range.endContainer, range.endOffset);
        }
    };

    const adicionar = (arquivos, depoisDe) => {
        if (!editavel) return;
        const recebidos = Array.from(arquivos ?? []);
        const aceitos = recebidos.filter((arquivo) => aceita(arquivo, accept));
        const recusados = recebidos.filter((arquivo) => !aceitos.includes(arquivo));
        if (recusados.length) toast.warning(`Este campo não aceita: ${recusados.map((arquivo) => arquivo.name).join(', ')}`);
        // Com limite cheio, o anexo novo não entra, e o aviso diz por quê.
        const cabem = maxAnexos ? Math.max(0, maxAnexos - anexos.length) : aceitos.length;
        const entram = aceitos.slice(0, cabem);
        if (entram.length < aceitos.length) {
            toast.warning(`Este campo aceita até ${maxAnexos} ${maxAnexos === 1 ? 'anexo' : 'anexos'}. Tire um para colocar outro.`);
        }
        if (!entram.length) return;
        // Id provisório, em sequência entre os anexos novos, até o back gravar e devolver o id real.
        const proximo = Math.max(0, ...anexos.filter(arquivoDe).map((anexo) => Number(anexo.id) || 0)) + 1;
        const novos = entram.map((arquivo, i) => ({ id: proximo + i, arquivo, linha: null }));
        const editor = editorRef.current;
        const ancora = depoisDe === undefined ? blocoDoCursor() : depoisDe;
        const blocos = novos.filter((anexo) => vaiNoCorpo(anexo, anexosNoCorpo)).map(blocoDoAnexo);
        if (blocos.length) {
            if (ancora && editor.contains(ancora)) ancora.after(...blocos);
            else blocos.forEach((bloco) => editor.appendChild(bloco));
            blocoGuardadoRef.current = blocos[blocos.length - 1];
        }
        emitir(novos);
    };

    const removerDoRodape = (anexo) => {
        if (onChange) onChange({ texto, anexos: anexos.filter((outro) => outro !== anexo) });
    };

    // Colar: o print vira anexo no ponto do cursor; texto entra sem a formatação de onde veio.
    const colar = (e) => {
        if (!editavel) return;
        const arquivos = Array.from(e.clipboardData?.files || []);
        const tipos = Array.from(e.clipboardData?.types || []);
        e.preventDefault();
        if (arquivos.length && !tipos.includes('text/plain')) {
            adicionar(arquivos.map((arquivo) => nomearColado(arquivo)));
            return;
        }
        const textoColado = e.clipboardData?.getData('text/plain');
        if (textoColado && document.execCommand) document.execCommand('insertText', false, textoColado);
    };

    // Arrastar: a imagem arrastada dentro do campo muda de lugar pelo próprio navegador, e o campo é remontado depois
    // para ela ocupar uma linha só dela. O arrasto interno é conferido antes do arquivo porque o Chrome manda a imagem
    // arrastada também como arquivo, e ela voltaria como anexo novo, duplicada. Arquivo de fora vira anexo onde caiu;
    // outro conteúdo de fora não entra.
    const temArquivo = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const arrastar = (e) => {
        if (!editavel || arrastoInternoRef.current) return;
        e.preventDefault();
        if (temArquivo(e)) setArrastando(true);
    };
    const sair = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setArrastando(false);
    };
    const soltar = (e) => {
        setArrastando(false);
        if (!editavel) return;
        if (arrastoInternoRef.current) {
            setTimeout(() => {
                emitir();
                emitidoRef.current = null;
            }, 0);
            return;
        }
        e.preventDefault();
        if (temArquivo(e)) {
            const range = document.caretRangeFromPoint ? document.caretRangeFromPoint(e.clientX, e.clientY) : null;
            adicionar(e.dataTransfer.files, range ? blocoDe(range.startContainer, range.startOffset) : null);
        }
    };

    // A imagem abre na prévia de arquivos: na leitura com um clique, editando com dois, porque o clique simples
    // seleciona a imagem para arrastar ou apagar. A imagem que o navegador recriou ao arrastar é achada pelo src.
    const abrirPrevia = (e) => {
        if (e.target.tagName !== 'IMG') return;
        const chave = e.target.dataset.anexo;
        const anexo = anexos.find((outro) => (chave ? chaveDe(outro) === chave : linkDe(outro) === e.target.getAttribute('src')));
        if (anexo) setPrevia(anexo);
    };

    // O Enter quebra a linha aqui, como num textarea, e não sobe para a tela: o form que usa o Enter para pular de
    // campo (o do evento faz isso) cancelaria a linha nova.
    const manterEnter = (e) => {
        if (e.key === 'Enter') e.stopPropagation();
    };

    const escolher = (e) => {
        adicionar(e.target.files);
        e.target.value = '';
    };

    const etiqueta = (anexo) => {
        const nome = nomeDoAnexo(anexo);
        const link = linkDe(anexo);
        const Icone = iconeDoAnexo(anexo);
        const tamanho = arquivoDe(anexo) ? tamanhoLegivel(arquivoDe(anexo).size) : null;
        return (
            <span className="fin-pp__obs-anexo" key={chaveDe(anexo)}>
                <Icone size={13} className="fin-pp__obs-anexo-ic" />
                {link
                    ? <a href={link} target="_blank" rel="noopener noreferrer" title={nome}>{nome}</a>
                    : <span className="fin-pp__obs-anexo-nome" title={nome}>{nome}</span>}
                {tamanho ? <span className="fin-pp__obs-anexo-tam">{tamanho}</span> : null}
                {editavel ? (
                    <button type="button" className="fin-pp__obs-remover" onClick={() => removerDoRodape(anexo)} title={`Remover ${nome}`} aria-label={`Remover ${nome}`}>
                        <FaTimes size={10} />
                    </button>
                ) : null}
            </span>
        );
    };

    let dica = null;
    if (corpo.length) dica = 'Arraste a imagem para mudar de lugar; dois cliques para ver; selecione e aperte Delete para tirar';
    else if (!anexos.length) dica = 'Cole (Ctrl+V) ou arraste prints e arquivos no campo';

    return (
        <div
            className={'fin-pp fin-pp__obs' + (arrastando ? ' is-dragging' : '') + (disabled ? ' is-disabled' : '')
                + (editavel ? '' : ' is-leitura') + (semCaixa ? ' fin-pp__obs--sem-caixa' : '')}
            onDragEnter={arrastar}
            onDragOver={arrastar}
            onDragLeave={sair}
            onDrop={soltar}
        >
            <div
                ref={editorRef}
                className="fin-pp__obs-editor"
                contentEditable={editavel}
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-readonly={!editavel}
                data-placeholder={editavel ? placeholder : 'Sem observação'}
                onInput={() => emitir()}
                onPaste={colar}
                onClick={editavel ? undefined : abrirPrevia}
                onDoubleClick={editavel ? abrirPrevia : undefined}
                onKeyDown={manterEnter}
                onKeyUp={guardarCursor}
                onMouseUp={guardarCursor}
                onBlur={guardarCursor}
                onDragStart={() => { arrastoInternoRef.current = true; }}
                onDragEnd={() => { arrastoInternoRef.current = false; }}
            />
            {(rodape.length > 0 || editavel) && (
                <div className="fin-pp__obs-rodape">
                    {rodape.map(etiqueta)}
                    {editavel && (
                        <span className="fin-pp__obs-acoes">
                            {dica ? <span className="fin-pp__obs-dica">{dica}</span> : null}
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => inputRef.current?.click()}>
                                <FaPaperclip size={12} /> Anexar
                            </button>
                        </span>
                    )}
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                multiple={maxAnexos !== 1}
                accept={accept ?? undefined}
                disabled={!editavel}
                style={{ display: 'none' }}
                onChange={escolher}
            />
            <VisualizarArquivoModal
                show={!!previa}
                onHide={() => setPrevia(null)}
                nome={previa ? nomeDoAnexo(previa) : ''}
                localFile={arquivoDe(previa) || undefined}
                url={previa && !arquivoDe(previa) ? linkDe(previa) : undefined}
            />
        </div>
    );
}
